import postHog from 'posthog-js';
import appConfig from '@/common/appConfig';
import { bytesToUnitObject } from '@/common/utils/formatter.number';
import { debounce } from 'lodash-es';

export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  postHog.capture(eventName, properties);
};

export const setPersonProperties = (properties?: Record<string, unknown>) => {
  postHog.setPersonProperties(properties);
};

export const updateUser = (user: any, roleInfo: { isSystemAdmin: boolean | undefined; isDomainAdmin: boolean | undefined }, additionalProps: Record<string, any> = {}) => {
  try {
    const distinctId = postHog.get_distinct_id();

    if (user?.id) {
      let displayName = `project_member_${distinctId}`;

      if (roleInfo.isSystemAdmin) {
        displayName = `admin_${distinctId}`;
      } else if (roleInfo.isDomainAdmin) {
        displayName = `domain_admin_${distinctId}`;
      }

      setPersonProperties({
        vz_app_env: appConfig.app_env,
        vz_initially_logged_in: true,
        vz_domain_id: user.domain_id,
        vz_project_id: user.project_id,
        vz_roles: user.roles?.map(role => role.id),
        vz_is_superuser: user.is_superuser,
        vz_external_provider: user.external_provider,
        vz_display_name: displayName,
        ...additionalProps
      });
    } else {
      setPersonProperties({
        vz_app_env: appConfig.app_env,
        vz_initially_logged_in: false,
        vz_display_name: `not_authenticated_${distinctId}`,
        ...additionalProps
      });
    }
  } catch (err) {
    // Silently continue without telemetry metadata
  }
};

export const trackPageView = (pageName: string, properties?: Record<string, unknown>) => {
  trackEvent('page_viewed', {
    page: pageName,
    ...properties
  });
};

export const trackRouteChange = (to: any, from: any, properties?: Record<string, unknown>) => {
  trackEvent('$pageview', {
    $current_url: to.fullPath,
    $prev_url: from.fullPath,
    route_name: to.name,
    prev_route_name: from.name,
    ...properties
  });
};

export const trackButtonClick = (buttonName: string, properties?: Record<string, unknown>) => {
  trackEvent('button_clicked', {
    button: buttonName,
    ...properties
  });
};

export const trackFormSubmit = (formName: string, properties?: Record<string, unknown>) => {
  trackEvent('form_submitted', {
    form: formName,
    ...properties
  });
};

export const trackError = (error: Error, properties?: Record<string, unknown>) => {
  postHog.captureException(error, properties);
};

const bytesToMB = (bytes?: number): number => {
  if (!bytes) return 0;
  const result = bytesToUnitObject(bytes, { maxPow: 2, minPow: 2 });
  return typeof result === 'string' ? 0 : Math.round(result.valueNumeric);
};

export const calculateThroughputKbps = (fileSizeBytes: number, durationMs: number, success: boolean): number => {
  if (!success || !fileSizeBytes || !durationMs || durationMs <= 0) {
    return 0;
  }

  const fileSizeBits = fileSizeBytes * 8;
  const durationSeconds = durationMs / 1000;
  const bitsPerSecond = fileSizeBits / durationSeconds;
  const kilobitsPerSecond = bitsPerSecond / 1024;

  return Math.round(kilobitsPerSecond);
};

export const trackMemoryUsage = (memoryInfo: {
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
  jsHeapSizeLimit?: number;
  timestamp: number;
}, additionalProperties?: Record<string, unknown>) => {
  const usagePercentage = memoryInfo.usedJSHeapSize && memoryInfo.jsHeapSizeLimit
    ? Math.round((memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit) * 100)
    : null;

  const properties = {
    used_heap_size_mb: bytesToMB(memoryInfo.usedJSHeapSize),
    total_heap_size_mb: bytesToMB(memoryInfo.totalJSHeapSize),
    heap_size_limit_mb: bytesToMB(memoryInfo.jsHeapSizeLimit),
    usage_percentage: usagePercentage,
    ...additionalProperties
  };

  trackEvent('memory_usage_tracked', properties);
};

export const trackTimeToInteractive = (ttiValue: number, additionalProperties?: Record<string, unknown>) => {
  trackEvent('time_to_interactive', {
    tti_ms: Math.round(ttiValue),
    tti_seconds: Number((ttiValue / 1000).toFixed(2)),
    ...additionalProperties
  });
};

const isFiniteNumber = (value?: number | null): value is number => typeof value === 'number' && Number.isFinite(value);
const toSeconds = (durationMs: number): number => Number((durationMs / 1000).toFixed(2));
const assignIfFinite = (properties: Record<string, unknown>, key: string, value?: number | null) => {
  if (isFiniteNumber(value)) {
    properties[key] = value;
  }
};

export type DomContentLoadedTiming = {
  duration: number;
  domContentLoadedStart?: number | null;
  domContentLoadedEnd?: number | null;
  navigationStart?: number | null;
};

export const trackDomContentLoaded = (timing: DomContentLoadedTiming, additionalProperties?: Record<string, unknown>) => {
  const properties: Record<string, unknown> = {
    dom_content_loaded_ms: Math.round(timing.duration),
    dom_content_loaded_seconds: toSeconds(timing.duration),
    ...additionalProperties
  };

  assignIfFinite(properties, 'dom_content_loaded_start', timing.domContentLoadedStart);
  assignIfFinite(properties, 'dom_content_loaded_end', timing.domContentLoadedEnd);
  assignIfFinite(properties, 'navigation_start', timing.navigationStart);

  trackEvent('dom_content_loaded', properties);
};

export type FullyLoadedTiming = {
  duration: number;
  loadEventStart?: number | null;
  loadEventEnd?: number | null;
  navigationStart?: number | null;
};

export const trackFullyLoadedTime = (timing: FullyLoadedTiming, additionalProperties?: Record<string, unknown>) => {
  const properties: Record<string, unknown> = {
    fully_loaded_ms: Math.round(timing.duration),
    fully_loaded_seconds: toSeconds(timing.duration),
    ...additionalProperties
  };

  assignIfFinite(properties, 'load_event_start', timing.loadEventStart);
  assignIfFinite(properties, 'load_event_end', timing.loadEventEnd);
  assignIfFinite(properties, 'navigation_start', timing.navigationStart);

  trackEvent('page_fully_loaded', properties);
};

export type FramesPerSecondSample = {
  fps: number;
  frameCount: number;
  sampleDurationMs: number;
  sampleStartTime: number;
  sampleEndTime: number;
};

export const trackFramesPerSecond = (sample: FramesPerSecondSample, additionalProperties?: Record<string, unknown>) => {
  const fpsExact = Number(sample.fps.toFixed(2));
  const properties: Record<string, unknown> = {
    fps_avg: fpsExact,
    fps_rounded: Math.round(sample.fps),
    frame_count: sample.frameCount,
    sample_duration_ms: Math.round(sample.sampleDurationMs),
    sample_duration_seconds: toSeconds(sample.sampleDurationMs),
    sample_start_ms: Math.round(sample.sampleStartTime),
    sample_end_ms: Math.round(sample.sampleEndTime),
    low_fps: fpsExact < 55,
    ...additionalProperties
  };

  trackEvent('frames_per_second_sampled', properties);
};

export const trackLongTask = (taskInfo: {
  name: string;
  duration: number;
  startTime: number;
  entryType: string;
}, additionalProperties?: Record<string, unknown>) => {
  trackEvent('long_task_detected', {
    task_name: taskInfo.name,
    duration_ms: Math.round(taskInfo.duration),
    start_time: Math.round(taskInfo.startTime),
    entry_type: taskInfo.entryType,
    ...additionalProperties
  });
};

export const trackFileTransfer = (transferInfo: {
  operation: 'upload' | 'download';
  fileName: string;
  fileSize: number;
  duration: number;
  success: boolean;
  errorMessage?: string;
}, additionalProperties?: Record<string, unknown>) => {
  const throughputKbps = calculateThroughputKbps(transferInfo.fileSize, transferInfo.duration, transferInfo.success);

  trackEvent('file_transfer_completed', {
    operation: transferInfo.operation,
    file_name: transferInfo.fileName,
    file_size_bytes: transferInfo.fileSize,
    file_size_mb: bytesToMB(transferInfo.fileSize),
    duration_ms: Math.round(transferInfo.duration),
    duration_seconds: Number((transferInfo.duration / 1000).toFixed(2)),
    success: transferInfo.success,
    error_message: transferInfo.errorMessage,
    throughput_kbps: throughputKbps,
    ...additionalProperties
  });
};

export const trackLongOperation = (operationInfo: {
  operationName: string;
  duration: number;
  success: boolean;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
}, additionalProperties?: Record<string, unknown>) => {
  trackEvent('long_operation_completed', {
    operation_name: operationInfo.operationName,
    duration_ms: Math.round(operationInfo.duration),
    duration_seconds: Number((operationInfo.duration / 1000).toFixed(2)),
    success: operationInfo.success,
    error_message: operationInfo.errorMessage,
    metadata: operationInfo.metadata,
    ...additionalProperties
  });
};

export const isFeatureEnabled = (flag: string) => postHog.isFeatureEnabled(flag);

export const trackDebounced = debounce(trackEvent, 1000);

export default {
  trackEvent,
  trackDebounced,
  setPersonProperties,
  updateUser,
  trackPageView,
  trackRouteChange,
  trackButtonClick,
  trackFormSubmit,
  trackError,
  trackMemoryUsage,
  trackTimeToInteractive,
  trackLongTask,
  trackFileTransfer,
  trackLongOperation,
  calculateThroughputKbps,
  postHog,
  isFeatureEnabled,
  trackDomContentLoaded,
  trackFullyLoadedTime,
  trackFramesPerSecond
};
